// LCD de caracteres en 3D (paralela e I²C). La pantalla se dibuja píxel por píxel con la ROM del
// HD44780 y los caracteres propios (CGRAM) que manda el programa.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape } from '../kit.js';
import { lcdSize, LCD_COLORS, LCD_PCB_Y, LCD_I2C_PCB_Y, lcdHeaderX } from '../../parts/lcd.js';
import { PITCH } from '../../parts/registry.js';
import { glyphRows } from '../font5x7.js';

const PCB_T = 1.6;
const BEZEL_H = 7;

function mix(a, b, t) {
  const ca = new THREE.Color(a);
  const cb = new THREE.Color(b);
  return '#' + ca.lerp(cb, t).getHexString();
}

class ScreenPainter {
  constructor(size, colorKey) {
    this.size = size;
    this.colors = LCD_COLORS[colorKey] || LCD_COLORS.blue;
    const [ww, wh] = size.window;
    this.pxmm = 9;
    this.cv = document.createElement('canvas');
    this.cv.width = Math.round(ww * this.pxmm);
    this.cv.height = Math.round(wh * this.pxmm);
    this.g = this.cv.getContext('2d');
    this.tex = new THREE.CanvasTexture(this.cv);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.anisotropy = 8;
    const marginX = this.cv.width * 0.045;
    const marginY = this.cv.height * 0.1;
    this.u = Math.min((this.cv.width - 2 * marginX) / (size.cols * 6 - 1), (this.cv.height - 2 * marginY) / (size.rows * 9 - 1));
    this.x0 = (this.cv.width - this.u * (size.cols * 6 - 1)) / 2;
    this.y0 = (this.cv.height - this.u * (size.rows * 9 - 1)) / 2;
  }

  paint(state, blinkOn) {
    const { g, cv, u, colors, size } = this;
    const bl = state ? state.bl : 0;
    const powered = state && state.powered;
    const bg = mix(colors.bgOff, colors.bg, bl);
    g.fillStyle = bg;
    g.fillRect(0, 0, cv.width, cv.height);
    // Viñeta suave como el difusor de la luz de fondo
    const grad = g.createRadialGradient(cv.width / 2, cv.height / 2, cv.height * 0.2, cv.width / 2, cv.height / 2, cv.width * 0.6);
    grad.addColorStop(0, 'rgba(255,255,255,0.05)');
    grad.addColorStop(1, 'rgba(0,0,0,0.18)');
    g.fillStyle = grad;
    g.fillRect(0, 0, cv.width, cv.height);
    if (!powered) {
      this.tex.needsUpdate = true;
      return;
    }
    const lit = colors === LCD_COLORS.green ? colors.on : mix(colors.bgOff, colors.on, 0.35 + 0.65 * bl);
    const off = mix(bg, colors.off, 0.55);
    const show = state.on;
    const d = u * 0.84;
    for (let r = 0; r < size.rows; r++) {
      for (let c = 0; c < size.cols; c++) {
        const code = show ? state.cells[r * size.cols + c] : 0x20;
        let rows = null;
        if (code < 16) {
          const base = (code & 7) * 8;
          rows = state.cgram.slice(base, base + 8);
        } else rows = glyphRows(code) || (code === 0x20 ? null : [0x1f, 0x11, 0x11, 0x11, 0x11, 0x11, 0x1f, 0]);
        const isCursor = show && state.cursor && state.cursor[0] === r && state.cursor[1] === c;
        const block = isCursor && state.blink && blinkOn;
        const cx = this.x0 + c * 6 * u;
        const cy = this.y0 + r * 9 * u;
        for (let y = 0; y < 8; y++) {
          const bits = rows ? rows[y] || 0 : 0;
          for (let x = 0; x < 5; x++) {
            let on = (bits >> (4 - x)) & 1;
            if (block) on = 1;
            if (isCursor && state.underline && y === 7) on = 1;
            g.fillStyle = on ? lit : off;
            g.fillRect(cx + x * u, cy + y * u, d, d);
          }
        }
      }
    }
    this.tex.needsUpdate = true;
  }
}

function pcbSilk(size, labels, i2c) {
  const { c, g } = canvas(size.w, size.d, 10);
  const X = (x) => x + size.w / 2;
  const Y = (z) => z + size.d / 2;
  g.fillStyle = '#1c6b33';
  g.fillRect(0, 0, size.w, size.d);
  g.strokeStyle = 'rgba(8,60,26,0.6)';
  g.lineWidth = 0.3;
  for (let i = 0; i < 16; i++) {
    g.beginPath();
    g.moveTo(X(lcdHeaderX(size) + i * PITCH), Y(-size.d / 2 + 2.6));
    g.lineTo(X(lcdHeaderX(size) + i * PITCH), Y(-size.d / 2 + 6));
    g.lineTo(X(-size.w / 2 + 14 + i * 3.4), Y(size.d / 2 - 4));
    g.stroke();
  }
  if (!i2c) {
    labels.forEach((l, i) => text(g, l, X(lcdHeaderX(size) + i * PITCH), Y(-size.d / 2 + 6.2), { size: 1.1, color: '#e8efe6', weight: 700, rotate: -Math.PI / 2, align: 'left' }));
  }
  text(g, '1', X(lcdHeaderX(size)) - 2.4, Y(-size.d / 2 + 2.6), { size: 1.3, color: '#e8efe6', weight: 700 });
  text(g, '16', X(lcdHeaderX(size) + 15 * PITCH) + 2.8, Y(-size.d / 2 + 2.6), { size: 1.3, color: '#e8efe6', weight: 700 });
  const t = texture(c);
  return t;
}

function buildModule(def, props, { i2c }) {
  const size = lcdSize(props);
  const colorKey = props.color || 'blue';
  const pcbY = i2c ? LCD_I2C_PCB_Y : LCD_PCB_Y;
  const top = pcbY + PCB_T;
  const b = new Builder();
  const [hx, hz] = size.holes;
  const holes = [[-hx, -hz, 1.3], [hx, -hz, 1.3], [-hx, hz, 1.3], [hx, hz, 1.3]];
  const labels = ['VSS', 'VDD', 'V0', 'RS', 'RW', 'E', 'D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'A', 'K'];
  b.root.add(extrudeBoard(boardShape(size.w, size.d, { r: 0.8 }), PCB_T, pcbSilk(size, labels, i2c), '#155a29', { holes, w: size.w, d: size.d, y0: pcbY }));
  for (const [x, z] of holes) b.add(new THREE.RingGeometry(1.3, 2.3, 20).rotateX(-Math.PI / 2), M.tin, { x, y: top + 0.02, z });

  // Marco metálico con la ventana de la pantalla
  const [bw, bd] = size.bezel;
  const [ww, wd] = size.window;
  const bz = size.bezelZ;
  const frame = mat('#2b2e34', { roughness: 0.38, metalness: 0.65 });
  b.box(bw, BEZEL_H, (bd - wd) / 2, frame, 0, top, bz - (bd + wd) / 4);
  b.box(bw, BEZEL_H, (bd - wd) / 2, frame, 0, top, bz + (bd + wd) / 4);
  b.box((bw - ww) / 2, BEZEL_H, wd, frame, -(bw + ww) / 4, top, bz);
  b.box((bw - ww) / 2, BEZEL_H, wd, frame, (bw + ww) / 4, top, bz);
  b.box(ww + 0.4, BEZEL_H - 0.9, wd + 0.4, mat('#0c0e12', { roughness: 0.5 }), 0, top, bz);
  // Lateral del difusor de la luz de fondo
  b.box(2.4, 3.2, bd - 4, mat('#dfe6ea', { roughness: 0.4 }), bw / 2 + 1.2, top, bz);

  // Conector de 16 pines (macho, hacia arriba)
  const x0 = lcdHeaderX(size);
  const zH = -size.d / 2 + 2.6;
  if (!i2c) {
    b.box(16 * PITCH, 2.5, PITCH, M.blackPlastic, x0 + 7.5 * PITCH, top, zH);
    for (let i = 0; i < 16; i++) b.box(0.64, 8.6, 0.64, M.gold, x0 + i * PITCH, top, zH);
  } else {
    // Adaptador I²C por debajo: PCB negro, PCF8574, potenciómetro de contraste y el conector lateral.
    const ax0 = x0 - 2;
    const aw = 16 * PITCH + 4;
    const azc = -size.d / 2 + 9.5;
    b.box(aw, PCB_T, 19, mat('#141517', { roughness: 0.55 }), ax0 + aw / 2, 1.5, azc);
    for (let i = 0; i < 16; i++) b.box(0.64, pcbY - 3.1, 0.64, M.gold, x0 + i * PITCH, 3.1, zH);
    b.box(16 * PITCH, 2.5, PITCH, M.blackPlastic, x0 + 7.5 * PITCH, 3.1, zH);
    b.box(10, 2.6, 7.6, M.chip, x0 + 18, 3.1, azc + 2.2);
    b.box(6.6, 4.2, 6.6, mat('#1f5fd2', { roughness: 0.4 }), x0 + 32, 3.1, azc + 2.6);
    b.cyl(1.6, 1.6, 0.6, mat('#e9e3cf', { roughness: 0.4 }), x0 + 32, 7.3, azc + 2.6, 16);
    b.box(PITCH * 0.8, 2.5, 4 * PITCH, M.blackPlastic, ax0 + 1.4, 3.1, azc);
    for (let i = 0; i < 4; i++) {
      const z = azc + (i - 1.5) * PITCH;
      b.box(7.5, 0.64, 0.64, M.gold, ax0 - 2.2, 4.0, z);
    }
  }
  b.build();

  // Pantalla
  const painter = new ScreenPainter(size, colorKey);
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(ww, wd).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: painter.tex }));
  screen.position.set(0, top + BEZEL_H - 0.85, bz);
  b.root.add(screen);
  const glass = new THREE.Mesh(
    new THREE.PlaneGeometry(ww, wd).rotateX(-Math.PI / 2),
    new THREE.MeshStandardMaterial({ color: '#ffffff', transparent: true, opacity: 0.06, roughness: 0.05, metalness: 0.2 }),
  );
  glass.position.set(0, top + BEZEL_H - 0.4, bz);
  glass.userData.noPick = false;
  b.root.add(glass);

  let state = null;
  let blinkOn = true;
  let blinkT = 0;
  painter.paint(null, true);
  return {
    root: b.root,
    update(v) {
      state = v;
      painter.paint(state, blinkOn);
    },
    animate(dt) {
      if (!state || !state.powered || !state.blink || !state.cursor) return false;
      blinkT += dt;
      if (blinkT > 0.41) {
        blinkT = 0;
        blinkOn = !blinkOn;
        painter.paint(state, blinkOn);
      }
      return true;
    },
    reset() {
      state = null;
      painter.paint(null, true);
    },
    dispose() {
      painter.tex.dispose();
    },
  };
}

export const MODELS = {
  lcd: (def, props) => buildModule(def, props, { i2c: false }),
  'lcd-i2c': (def, props) => buildModule(def, props, { i2c: true }),
};
